// mobile_v1: responsive — tabs full-width + form 1 col + KB cards
import React, { useState, useEffect } from 'react';
import API from '../api';
import { Bot, Plus, Trash2, Edit2, Save, X, AlertTriangle, CheckCircle, Download, Layers, Radio, Send } from 'lucide-react';
import toast from 'react-hot-toast';

const emptyKB = { topic: '', keywords: '', answer: '', need_alert: false, telegram_id: '', kb_set_id: '', is_game_type: false, image_url: '' }; /* bot_kb_image_v1 */
const emptySet = { name: '', channel_ids: [], telegram_id: '' };

const TYPE_ICONS = { line: '💚', telegram: '✈️', facebook: '💙', whatsapp: '💬', webchat: '🌐' };

export default function BotTraining() {
  const [items, setItems] = useState([]);
  const [kbSets, setKbSets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [telegrams, setTelegrams] = useState([]);
  const [form, setForm] = useState(emptyKB);
  const [setForm2, setSetForm] = useState(emptySet);
  const [editId, setEditId] = useState(null);
  const [imgUploading, setImgUploading] = useState(false); /* bot_kb_image_v1 */
  const [editSetId, setEditSetId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('kb');
  const [filterSet, setFilterSet] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const load = async () => {
    const [kb, sets, ch, tg] = await Promise.all([
      API.get('/api/botkb'),
      API.get('/api/kbsets'),
      API.get('/api/channels'),
      API.get('/api/telegrams'),
    ]);
    setItems(kb.data);
    setKbSets(sets.data);
    setChannels(ch.data);
    setTelegrams(tg.data);
  };

  useEffect(() => { load(); }, []);

  /* kb_match_trace_v1: มาจากลิงก์ "แก้คำตอบนี้" ในหน้าทดสอบบอท (?edit=<id>)
     เปิดฟอร์มแก้ไขให้เลย ร้านจะได้ไม่ต้องไล่หาเองว่าคำตอบที่เห็นอยู่รายการไหน
     ลบพารามิเตอร์ทิ้งหลังใช้ ไม่งั้นกด refresh แล้วเด้งกลับมาแก้ซ้ำทั้งที่แก้เสร็จไปแล้ว */
  const [pendingEdit, setPendingEdit] = useState(
    () => new URLSearchParams(window.location.search).get('edit')
  );
  useEffect(() => {
    if (!pendingEdit || !items.length) return;
    const item = items.find((i) => String(i.id) === String(pendingEdit));
    if (item) handleEdit(item);
    else toast.error('ไม่พบคำตอบนี้แล้ว อาจถูกลบไปก่อนหน้า');
    setPendingEdit(null);
    window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingEdit]);

  // KB CRUD
  const formRef = React.useRef(form);
  React.useEffect(() => { formRef.current = form; }, [form]);

  const handleSave = async () => {
    const currentForm = formRef.current;
    if (!currentForm.topic.trim() || !currentForm.keywords.trim() || !currentForm.answer.trim()) {
      toast.error('กรุณากรอกข้อมูลให้ครบ'); return;
    }
    setLoading(true);
    try {
      const payload = { ...currentForm, telegram_id: currentForm.telegram_id || null, kb_set_id: currentForm.kb_set_id || null };
      console.log('saving payload:', payload);
      if (editId) { await API.put(`/api/botkb/${editId}`, payload); toast.success('อัปเดตสำเร็จ'); }
      else { await API.post('/api/botkb', payload); toast.success('เพิ่มสำเร็จ'); }
      setForm(emptyKB); setEditId(null); load();
    } catch { toast.error('เกิดข้อผิดพลาด'); }
    setLoading(false);
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({ topic: item.topic, keywords: item.keywords.join(', '), answer: item.answer,
      need_alert: item.need_alert, telegram_id: item.telegram_id || '', kb_set_id: item.kb_set_id || '', image_url: item.image_url || '' }); /* bot_kb_image_v1 */
    setTab('kb'); window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ลบรายการนี้?')) return;
    await API.delete(`/api/botkb/${id}`); toast.success('ลบแล้ว'); load();
  };

  /* kbToggle_v2 */
  const toggleActive = async (item) => {
    try {
      const newActive = !(item.is_active !== false);
      await API.put(`/api/botkb/${item.id}`, {
        topic: item.topic,
        keywords: item.keywords,
        answer: item.answer,
        need_alert: item.need_alert,
        is_active: newActive,
        telegram_id: item.telegram_id,
        kb_set_id: item.kb_set_id,
        is_game_type: item.is_game_type,
      });
      toast.success(newActive ? 'เปิดใช้งานแล้ว' : 'ปิดใช้งานแล้ว');
      load();
    } catch (err) {
      /* kb_toggle_error_v1: show backend error message */
      toast.error(err?.response?.data?.error || 'เปลี่ยนสถานะไม่สำเร็จ');
    }
  };

  const handleImport = async () => {
    if (!window.confirm('นำเข้า Template Casino จาก Master Template?')) return;
    setImporting(true);
    try {
      const { data } = await API.post('/api/botkb/import-template', {});
      toast.success(`นำเข้าสำเร็จ ${data.count} หัวข้อ`); load();
    } catch(err) { toast.error(err.response?.data?.error || 'นำเข้าไม่สำเร็จ'); }
    setImporting(false);
  };

  // KB Set CRUD
  const handleSaveSet = async () => {
    if (!setForm2.name.trim()) { toast.error('กรุณาใส่ชื่อ KB Set'); return; }
    setLoading(true);
    try {
      const payload = { ...setForm2, telegram_id: setForm2.telegram_id || null };
      if (editSetId) { await API.put(`/api/kbsets/${editSetId}`, payload); toast.success('อัปเดต KB Set สำเร็จ'); }
      else { await API.post('/api/kbsets', payload); toast.success('สร้าง KB Set สำเร็จ'); }
      setSetForm(emptySet); setEditSetId(null); load();
    } catch { toast.error('เกิดข้อผิดพลาด'); }
    setLoading(false);
  };

  const handleDeleteSet = async (id) => {
    if (!window.confirm('ลบ KB Set นี้? (KB ภายใน Set จะไม่ถูกลบ)')) return;
    await API.delete(`/api/kbsets/${id}`); toast.success('ลบแล้ว'); load();
  };

  const filtered = items.filter(i => {
    const matchSearch = !search || i.topic.includes(search) || i.keywords.some(k => k.includes(search));
    const matchSet = !filterSet || 
      (filterSet === 'none' ? !i.kb_set_id : i.kb_set_id === filterSet);
    return matchSearch && matchSet;
  });

  const getSetName = (id) => kbSets.find(s => s.id === id)?.name || '— ไม่มี Set —';
  const getTgName = (id) => telegrams.find(t => t.id === id)?.name || '-';

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 28px', background: '#f8fafc', minHeight: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Bot size={isMobile ? 22 : 26} color="#0ea5e9" />
          <div>
            <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b' }}>เทรนบอท</h1>
            <p style={{ margin: 0, fontSize: isMobile ? 12 : 13, color: '#64748b' }}>จัดการ KB Sets และหัวข้อคำตอบ</p>
          </div>
        </div>
        <button onClick={handleImport} disabled={importing}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8,
            padding: isMobile ? '12px 16px' : '10px 16px',
            fontSize: isMobile ? 14 : 13, fontWeight: 600, cursor: 'pointer',
          }}>
          <Download size={15} />{importing ? 'กำลังนำเข้า...' : '⚡ Template Casino'}
        </button>
      </div>

      {/* Tabs — full-width บน mobile */}
      <div style={{
        display: 'flex',
        gap: isMobile ? 0 : 4,
        marginBottom: isMobile ? 16 : 24,
        background: isMobile ? '#fff' : 'transparent',
        borderRadius: isMobile ? 10 : 0,
        padding: isMobile ? 4 : 0,
        boxShadow: isMobile ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
      }}>
        {[['kb', '📚 KB หัวข้อ'], ['sets', '📦 KB Sets']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{
              flex: isMobile ? 1 : 'none',
              padding: isMobile ? '10px 16px' : '9px 20px',
              borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: isMobile ? 13 : 13, fontWeight: 600,
              background: tab === key ? '#0ea5e9' : (isMobile ? 'transparent' : '#fff'),
              color: tab === key ? '#fff' : '#64748b',
              boxShadow: (tab === key || isMobile) ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
              transition: 'all 0.15s',
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* KB Sets Tab */}
      {tab === 'sets' && (
        <div>
          {/* Set Form */}
          <div style={{
            background: '#fff', borderRadius: 14,
            padding: isMobile ? 16 : 24,
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            marginBottom: isMobile ? 16 : 24,
          }}>
            <h2 style={{
              margin: '0 0 16px',
              fontSize: isMobile ? 14 : 15,
              fontWeight: 600, color: '#1e293b',
            }}>
              {editSetId ? '✏️ แก้ไข KB Set' : '➕ สร้าง KB Set ใหม่'}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 12 : 14,
              marginBottom: isMobile ? 12 : 14,
            }}>
              <div>
                <Label>ชื่อ KB Set</Label>
                <input value={setForm2.name} onChange={e => setSetForm({ ...setForm2, name: e.target.value })}
                  placeholder="เช่น คาสิโน, ร้านอาหาร, ร้านค้าออนไลน์"
                  style={inputStyle(isMobile)} />
              </div>
              <div>
                <Label>
                  <Send size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Telegram แจ้งเตือน
                </Label>
                <select value={setForm2.telegram_id} onChange={e => setSetForm({ ...setForm2, telegram_id: e.target.value })}
                  style={inputStyle(isMobile)}>
                  <option value="">— ไม่แจ้งเตือน —</option>
                  {telegrams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Label>
                <Radio size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />เชื่อม Channels (ไม่เลือก = ทุก Channel)
              </Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {channels.map(ch => (
                  <label key={ch.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: isMobile ? '8px 14px' : '7px 14px',
                    borderRadius: 8,
                    border: `1px solid ${setForm2.channel_ids.includes(ch.id) ? '#0ea5e9' : '#e2e8f0'}`,
                    background: setForm2.channel_ids.includes(ch.id) ? '#f0f9ff' : '#f8fafc',
                    cursor: 'pointer',
                    fontSize: isMobile ? 13 : 13,
                  }}>
                    <input type="checkbox" checked={setForm2.channel_ids.includes(ch.id)}
                      onChange={e => {
                        const ids = e.target.checked
                          ? [...setForm2.channel_ids, ch.id]
                          : setForm2.channel_ids.filter(id => id !== ch.id);
                        setSetForm({ ...setForm2, channel_ids: ids });
                      }} />
                    {TYPE_ICONS[ch.type] || '📡'} {ch.name}
                  </label>
                ))}
                {channels.length === 0 && <span style={{ fontSize: 13, color: '#94a3b8' }}>ยังไม่มี Channel — ไปเพิ่มที่เมนู Channels ก่อน</span>}
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: 8,
              justifyContent: isMobile ? 'stretch' : 'flex-end',
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              {editSetId && <button onClick={() => { setSetForm(emptySet); setEditSetId(null); }}
                style={{
                  padding: isMobile ? '12px 16px' : '9px 16px',
                  borderRadius: 8, background: '#f1f5f9', border: 'none',
                  cursor: 'pointer', fontSize: 14, fontWeight: 500,
                  order: isMobile ? 2 : 1,
                }}>ยกเลิก</button>}
              <button onClick={handleSaveSet} disabled={loading}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: isMobile ? '12px 20px' : '9px 20px',
                  borderRadius: 8, background: '#0ea5e9', color: '#fff',
                  border: 'none', cursor: 'pointer',
                  fontSize: 14, fontWeight: 600,
                  order: isMobile ? 1 : 2,
                }}>
                <Save size={14} />{loading ? 'กำลังบันทึก...' : 'บันทึก KB Set'}
              </button>
            </div>
          </div>

          {/* Set List */}
          <div style={{ display: 'grid', gap: 12 }}>
            {kbSets.length === 0 && (
              <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                <Layers size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                <p>ยังไม่มี KB Set สร้างใหม่ด้านบนได้เลย</p>
              </div>
            )}
            {kbSets.map(s => (
              <div key={s.id} style={{
                background: '#fff', borderRadius: 12,
                padding: isMobile ? 14 : '18px 24px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                borderLeft: '4px solid #0ea5e9',
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: isMobile ? 10 : 16,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#1e293b', fontSize: isMobile ? 15 : 15 }}>{s.name}</span>
                      <span style={{ background: '#f0f9ff', color: '#0ea5e9', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                        {s.kb_count} หัวข้อ
                      </span>
                      {s.telegram_name && (
                        <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                          📲 {s.telegram_name}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {s.channels && s.channels.length > 0
                        ? s.channels.map(ch => (
                            <span key={ch.id} style={{ background: '#f8fafc', color: '#64748b', borderRadius: 6, padding: '2px 8px', fontSize: 12, border: '1px solid #e2e8f0' }}>
                              {TYPE_ICONS[ch.type] || '📡'} {ch.name}
                            </span>
                          ))
                        : <span style={{ fontSize: 12, color: '#94a3b8' }}>ใช้กับทุก Channel</span>
                      }
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: 8,
                    justifyContent: isMobile ? 'flex-end' : 'flex-start',
                  }}>
                    <button onClick={() => { setEditSetId(s.id); setSetForm({ name: s.name, channel_ids: s.channel_ids || [], telegram_id: s.telegram_id || '' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      style={iconBtn('#f1f5f9', isMobile)}>
                      <Edit2 size={14} color="#64748b" />
                    </button>
                    <button onClick={() => handleDeleteSet(s.id)}
                      style={iconBtn('#fff1f2', isMobile)}>
                      <Trash2 size={14} color="#ef4444" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KB Tab */}
      {tab === 'kb' && (
        <div>
          {/* KB Form */}
          <div style={{
            background: '#fff', borderRadius: 14,
            padding: isMobile ? 16 : 24,
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            marginBottom: isMobile ? 16 : 24,
          }}>
            <h2 style={{
              margin: '0 0 16px',
              fontSize: isMobile ? 14 : 15,
              fontWeight: 600, color: '#1e293b',
            }}>
              {editId ? '✏️ แก้ไขรายการ' : '➕ เพิ่มรายการใหม่'}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
              gap: 12, marginBottom: 12,
            }}>
              <div>
                <Label>หัวข้อ</Label>
                <input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })}
                  placeholder="เช่น วิธีฝากเงิน"
                  style={inputStyle(isMobile)} />
              </div>
              <div>
                <Label>คีย์เวิร์ด <span style={{ color: '#94a3b8', fontWeight: 400 }}>(คั่นด้วยจุลภาค)</span></Label>
                <input value={form.keywords} onChange={e => setForm({ ...form, keywords: e.target.value })}
                  placeholder="เช่น ฝากเงิน, วิธีฝาก, เติมเงิน"
                  style={inputStyle(isMobile)} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Label>คำตอบ</Label>
              <textarea value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })}
                placeholder="ข้อความที่บอทจะตอบ..." rows={isMobile ? 4 : 3}
                style={{ ...inputStyle(isMobile), resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            {/* kb_image_upload_ui (bot_kb_image_v1) */}
            <div style={{ marginBottom: 12 }}>
              <Label>รูปภาพแนบ (ไม่บังคับ)</Label>
              {form.image_url ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                  <img src={form.image_url} alt="kb" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <button type="button" onClick={() => setForm(prev => ({ ...prev, image_url: '' }))} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 13, cursor: 'pointer' }}>ลบรูป</button>
                </div>
              ) : (
                <input type="file" accept="image/png,image/jpeg,image/webp" disabled={imgUploading} onChange={async (e) => {
                  const file = e.target.files && e.target.files[0]; if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { toast.error('รูปใหญ่เกิน 5MB'); return; }
                  setImgUploading(true);
                  try {
                    const b64 = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file); });
                    const { data } = await API.post('/api/botkb/upload-image', { image_base64: b64 });
                    setForm(prev => ({ ...prev, image_url: data.url }));
                    toast.success('อัปโหลดรูปสำเร็จ');
                  } catch { toast.error('อัปโหลดรูปไม่สำเร็จ'); }
                  setImgUploading(false); e.target.value = '';
                }} style={{ marginTop: 6, fontSize: 13 }} />
              )}
              {imgUploading && <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>กำลังอัปโหลด...</span>}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 12, marginBottom: 12,
            }}>
              <div>
                <Label>
                  <Layers size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />KB Set (ไม่เลือก = ทุก Channel)
                </Label>
                <select value={form.kb_set_id || ''} onChange={e => { const val = e.target.value; setForm(prev => ({ ...prev, kb_set_id: val })); }}
                  style={inputStyle(isMobile)}>
                  <option value="">— ไม่สังกัด Set ใด —</option>
                  {kbSets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label>
                  <Send size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Telegram (override จาก Set)
                </Label>
                <select value={form.telegram_id} onChange={e => setForm({ ...form, telegram_id: e.target.value })}
                  style={inputStyle(isMobile)}>
                  <option value="">— ใช้ของ Set —</option>
                  {telegrams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            {/* Checkboxes + Buttons */}
            <div style={{
              display: 'flex',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'space-between',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 14 : 0,
            }}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 16 }}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  cursor: 'pointer',
                  fontSize: isMobile ? 14 : 13,
                  padding: isMobile ? '8px 0' : 0,
                }}>
                  <input type="checkbox" checked={form.need_alert}
                    onChange={e => setForm({ ...form, need_alert: e.target.checked })}
                    style={{ width: isMobile ? 18 : 16, height: isMobile ? 18 : 16 }} />
                  <AlertTriangle size={14} color="#f59e0b" />แจ้งเตือน Telegram
                </label>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  cursor: 'pointer',
                  fontSize: isMobile ? 14 : 13,
                  padding: isMobile ? '8px 0' : 0,
                }}>
                  <input type="checkbox" checked={form.is_game_type}
                    onChange={e => setForm({ ...form, is_game_type: e.target.checked })}
                    style={{ width: isMobile ? 18 : 16, height: isMobile ? 18 : 16 }} />
                  🎰 แนะนำเกม (สุ่มแสดงเกม)
                </label>
              </div>
              <div style={{
                display: 'flex',
                gap: 8,
                flexDirection: isMobile ? 'column' : 'row',
              }}>
                {editId && <button onClick={() => { setForm(emptyKB); setEditId(null); }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8,
                    padding: isMobile ? '12px 16px' : '9px 16px',
                    fontSize: isMobile ? 14 : 13, cursor: 'pointer',
                    order: isMobile ? 2 : 1,
                  }}>
                  <X size={14} />ยกเลิก
                </button>}
                <button onClick={handleSave} disabled={loading}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8,
                    padding: isMobile ? '12px 20px' : '9px 20px',
                    fontSize: isMobile ? 14 : 13, fontWeight: 600, cursor: 'pointer',
                    order: isMobile ? 1 : 2,
                  }}>
                  <Save size={14} />{loading ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
              </div>
            </div>
          </div>

          {/* Filter + List */}
          <div style={{
            background: '#fff', borderRadius: 14,
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: isMobile ? '12px 14px' : '16px 24px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? 8 : 12,
            }}>
              <h2 style={{
                margin: 0,
                fontSize: isMobile ? 14 : 15,
                fontWeight: 600, color: '#1e293b',
                flex: 1,
              }}>รายการ KB ({filtered.length})</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={filterSet} onChange={e => setFilterSet(e.target.value)}
                  style={{
                    padding: isMobile ? '10px 12px' : '7px 12px',
                    borderRadius: 8, border: '1px solid #e2e8f0',
                    fontSize: isMobile ? 14 : 13, color: '#475569',
                    flex: isMobile ? '1 1 0' : 'none',
                    minWidth: 0,
                  }}>
                  <option value="">ทุก KB Set</option>
                  {kbSets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  <option value="none">ไม่มี Set</option>
                </select>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="ค้นหา..."
                  style={{
                    padding: isMobile ? '10px 12px' : '7px 12px',
                    borderRadius: 8, border: '1px solid #e2e8f0',
                    fontSize: isMobile ? 14 : 13,
                    width: isMobile ? 'auto' : 180,
                    flex: isMobile ? '1 1 0' : 'none',
                    minWidth: 0,
                    outline: 'none',
                  }} />
              </div>
            </div>

            {filtered.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
                <Bot size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                <p>ไม่มีรายการ KB</p>
              </div>
            ) : isMobile ? (
              /* mobile: KB Card list */
              <div>
                {filtered.map((item, i) => (
                  <KBCard key={item.id} item={item}
                    getSetName={getSetName} getTgName={getTgName}
                    onEdit={handleEdit} onToggle={toggleActive} onDelete={handleDelete}
                    isLast={i === filtered.length - 1} />
                ))}
              </div>
            ) : (
              /* desktop: ตารางเดิม */
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['หัวข้อ', 'KB Set', 'คีย์เวิร์ด', 'คำตอบ', 'Telegram', 'แจ้งเตือน', 'เปิด', ''].map((h, i) => (
                      <th key={i} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(item => (
                    <tr key={item.id} style={{ borderTop: '1px solid #f1f5f9', opacity: item.is_active === false ? 0.5 : 1, background: item.is_active === false ? '#f9fafb' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background = item.is_active === false ? '#f1f5f9' : '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = item.is_active === false ? '#f9fafb' : ''}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1e293b', fontSize: 14 }}>{item.topic}</td>
                      <td style={{ padding: '12px 16px' }}>
                        {item.kb_set_id
                          ? <span style={{ background: '#f0f9ff', color: '#0ea5e9', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>{getSetName(item.kb_set_id)}</span>
                          : <span style={{ color: '#94a3b8', fontSize: 12 }}>ทุก Channel</span>}
                      </td>
                      <td style={{ padding: '12px 16px', maxWidth: 200 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {item.keywords.slice(0, 3).map((kw, i) => (
                            <span key={i} style={{ background: '#f0f9ff', color: '#0ea5e9', borderRadius: 4, padding: '2px 7px', fontSize: 11 }}>{kw}</span>
                          ))}
                          {item.keywords.length > 3 && <span style={{ fontSize: 11, color: '#94a3b8' }}>+{item.keywords.length - 3}</span>}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569', maxWidth: 240 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.answer}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>
                        {item.telegram_id ? <span style={{ color: '#0ea5e9' }}>📲 {getTgName(item.telegram_id)}</span> : <span style={{ color: '#94a3b8' }}>ใช้ของ Set</span>}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {item.need_alert ? <AlertTriangle size={16} color="#f59e0b" /> : <CheckCircle size={16} color="#22c55e" />}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Toggle active={item.is_active !== false} onChange={() => toggleActive(item)} />
                      </td>
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                        <button onClick={() => handleEdit(item)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', marginRight: 6 }}>
                          <Edit2 size={13} color="#64748b" />
                        </button>
                        <button onClick={() => handleDelete(item.id)} style={{ background: '#fff1f2', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
                          <Trash2 size={13} color="#ef4444" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// mobile_v1: KB Card สำหรับ mobile
function KBCard({ item, getSetName, getTgName, onEdit, onToggle, onDelete, isLast }) {
  const isActive = item.is_active !== false;
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
      opacity: isActive ? 1 : 0.6,
      background: isActive ? '#fff' : '#fafafa',
    }}>
      {/* Top: หัวข้อ + toggle */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
            {item.topic}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {item.kb_set_id
              ? <span style={{ background: '#f0f9ff', color: '#0ea5e9', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>📦 {getSetName(item.kb_set_id)}</span>
              : <span style={{ background: '#f1f5f9', color: '#94a3b8', borderRadius: 6, padding: '2px 8px', fontSize: 11 }}>ทุก Channel</span>}
            {item.need_alert && (
              <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                ⚠️ แจ้ง TG
              </span>
            )}
            {item.is_game_type && (
              <span style={{ background: '#fce7f3', color: '#be185d', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                🎰 เกม
              </span>
            )}
          </div>
        </div>
        <Toggle active={isActive} onChange={() => onToggle(item)} />
      </div>

      {/* Keywords */}
      {item.keywords && item.keywords.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          {item.keywords.slice(0, 5).map((kw, i) => (
            <span key={i} style={{ background: '#f0f9ff', color: '#0ea5e9', borderRadius: 4, padding: '2px 7px', fontSize: 11 }}>{kw}</span>
          ))}
          {item.keywords.length > 5 && <span style={{ fontSize: 11, color: '#94a3b8' }}>+{item.keywords.length - 5}</span>}
        </div>
      )}

      {/* Answer preview */}
      <div style={{
        fontSize: 13, color: '#475569',
        background: '#f8fafc', padding: '8px 12px', borderRadius: 8,
        marginBottom: 10,
        lineHeight: 1.4,
        display: '-webkit-box',
        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {item.answer}
      </div>

      {/* Telegram */}
      {item.telegram_id && (
        <div style={{ fontSize: 11, color: '#0ea5e9', marginBottom: 10 }}>
          📲 {getTgName(item.telegram_id)} (override)
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onEdit(item)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: '#f1f5f9', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            fontSize: 13, color: '#64748b', fontWeight: 500,
          }}>
          <Edit2 size={14} /> แก้ไข
        </button>
        <button onClick={() => onDelete(item.id)}
          style={{
            padding: '10px 14px', borderRadius: 8,
            background: '#fff1f2', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <Trash2 size={14} color="#ef4444" />
        </button>
      </div>
    </div>
  );
}

const Toggle = ({ active, onChange }) => (
  <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 20, cursor: 'pointer', flexShrink: 0 }}
    title={active ? 'เปิดอยู่' : 'ปิดอยู่'}>
    <input type="checkbox" checked={active} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
    <span style={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: active ? '#22c55e' : '#cbd5e1',
      borderRadius: 20, transition: '0.2s',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: active ? 18 : 2,
        width: 16, height: 16, background: '#fff', borderRadius: '50%',
        transition: '0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </span>
  </label>
);

const Label = ({ children }) => (
  <label style={{
    fontSize: 12, fontWeight: 600, color: '#64748b',
    display: 'block', marginBottom: 4,
  }}>{children}</label>
);

const inputStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: isMobile ? 16 : 14,
  boxSizing: 'border-box',
  outline: 'none',
});

const iconBtn = (bg, isMobile) => ({
  background: bg, border: 'none', borderRadius: 8,
  padding: isMobile ? '10px 12px' : '6px 10px',
  cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minWidth: isMobile ? 40 : 'auto',
  minHeight: isMobile ? 40 : 'auto',
});
